import { Sentry } from '@/shared/lib/sentry'

/**
 * Violações de CSP vão para o Sentry como aviso (SR-10), uma vez por diretiva, origem
 * bloqueada e disposition (enforce/report) a cada carregamento de página. Só a origem do
 * recurso é enviada (sem path nem query, que podem carregar ids de paciente).
 */

export type CspViolationLike = Pick<
  SecurityPolicyViolationEvent,
  'effectiveDirective' | 'blockedURI' | 'disposition' | 'sourceFile'
>

export type CspReport = {
  key: string
  directive: string
  blocked: string
  disposition: string
}

const EXTENSION_SCHEME = /^(chrome|moz|safari(-web)?|ms-browser)-extension:/i

/** Esquemas que o navegador reporta sem `:` (ex.: Chrome manda `blob` para um Worker de blob:). */
const BARE_SCHEME = /^(blob|data|filesystem|mediastream)$/i

const toBlockedOrigin = (blockedURI: string): string => {
  if (!blockedURI || /^(inline|eval|wasm-eval|trusted-types-sink)$/.test(blockedURI)) return blockedURI || 'inline'
  if (BARE_SCHEME.test(blockedURI)) return `${blockedURI.toLowerCase()}:`
  try {
    const url = new URL(blockedURI)
    return url.protocol === 'data:' || url.protocol === 'blob:' ? url.protocol : url.origin
  } catch {
    return 'unknown'
  }
}

export function toCspReport(event: CspViolationLike): CspReport | null {
  if (EXTENSION_SCHEME.test(event.blockedURI) || EXTENSION_SCHEME.test(event.sourceFile ?? '')) return null
  const blocked = toBlockedOrigin(event.blockedURI)
  const directive = event.effectiveDirective || 'unknown'
  const disposition = event.disposition || 'unknown'
  /** Disposition na chave: um bloqueio real (enforce) não fica escondido atrás do report-only. */
  return { key: `${directive}|${blocked}|${disposition}`, directive, blocked, disposition }
}

let registered = false

export function registerCspViolationReporting(target: Pick<Document, 'addEventListener'> = document): void {
  if (registered) return
  registered = true
  const seen = new Set<string>()
  target.addEventListener('securitypolicyviolation', (event) => {
    const report = toCspReport(event as SecurityPolicyViolationEvent)
    if (!report || seen.has(report.key)) return
    seen.add(report.key)
    Sentry.captureMessage(`CSP: ${report.directive} bloqueou ${report.blocked}`, {
      level: 'warning',
      tags: { csp_directive: report.directive, csp_disposition: report.disposition },
      extra: { blocked: report.blocked },
    })
  })
}
